import type { Values } from '@ps/web-core-intl';
import { tryParse } from '@ps/web-core-utils';
import { parseISO } from 'date-fns';
import urlParse from 'url-parse';

import type {
  BaseMPNotification,
  ChestRewardMPNotification,
  MultiPurposeNotification,
  SplitWagerBonusMPNotification
} from '@swf/core/types/Notification';

import { ChestAssetType, NotificationType } from '../enums';

export const formatDate = (value: string, locale: string) => {
  const dateValue = value.replace(' ', 'T');
  return parseISO(dateValue).toLocaleDateString(locale, {
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

/**
 * Transform incoming fixed prize draw notification data.
 * Add the state to the type field to identify which view to display.
 * Add formatted draw date to attributes for display in text
 * @param notificationData
 * @param locale
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
const transformFixedPrizeDraw = (notificationDataMsg: Record<string, any>, locale: string) => {
  const {
    attributes: { draw: formattedDrawDate }
  } = notificationDataMsg;
  const { title, state, type } = notificationDataMsg;
  return {
    ...notificationDataMsg,
    type: `${type}.${state}`,
    title: state === 'lost' ? undefined : title,
    attributes: {
      ...notificationDataMsg.attributes,
      formattedDrawDate: formatDate(formattedDrawDate, locale),
      drawName: title
    }
  };
};

const transformNotificationData = (notificationDataMsg: Record<string, any>) => ({
  ...notificationDataMsg,
  type: `${notificationDataMsg.type}.${notificationDataMsg.state}`
});

/**
 * Return an absolute URL to the chest asset
 * @param type
 * @param cdnBase
 * @param environmentId
 * @param assetId
 */
const getChestAssetURL = (cdnBase: string, assetPath: string): string | null => {
  if (!cdnBase || cdnBase.length === 0 || !assetPath) {
    // mandatory parameters not populated, return null.
    return null;
  }
  // strip off leading or trailing slash
  cdnBase = cdnBase.endsWith('/') ? cdnBase.substring(0, cdnBase.length - 1) : cdnBase;
  assetPath = assetPath.startsWith('/') ? assetPath.substring(1) : assetPath;
  return `${cdnBase}/${assetPath}`;
};

/**
 * Transform incoming chest reward notification data
 * @param notificationData
 * @param locale
 * @param brand
 */
const transformChestReward = (notificationData: ChestRewardMPNotification) => {
  // remove availableOn or expiresOn when we get hideTimes flag
  const { hideTimes, attributes: attributeData } = notificationData;
  if (hideTimes === true) {
    attributeData.availableOn = undefined;
    attributeData.expiresOn = undefined;
  }

  // find any configured header images & place in the data
  const { metaData = [] } = attributeData;
  const transformedMetaData = metaData.map((metaItem) => ({
    ...metaItem,
    uriHint: JSON.parse(metaItem.uriHint as string)
  }));
  const headerMetaData = transformedMetaData.find(
    (d) => d.type === ChestAssetType.NotificationHeader
  );
  const cdnBase = 'https://starscrm.rationalcdn.com';
  let headerImageURL = '';
  if (headerMetaData) {
    const {
      uriHint: { path }
    } = headerMetaData;
    headerImageURL = getChestAssetURL(cdnBase, path) || '';
  }
  return {
    ...notificationData,
    type: `${notificationData.type}.${notificationData.state}`,
    headerImageURL,
    cdnBase,
    attributes: {
      ...attributeData,
      metaData: transformedMetaData
    }
  };
};

const transformInstantBonus = (
  notificationData: SplitWagerBonusMPNotification
): SplitWagerBonusMPNotification => {
  const {
    attributes,
    notificationId,
    rollState,
    attributes: {
      initialAmount,
      costCash,
      pointsToClear,
      maxWinnings,
      supportedGamesJson,
      gameMaxBet
    }
  } = notificationData;
  const bonusTotal = initialAmount;
  const maxIntValue = 2147483647;
  return {
    ...notificationData,
    attributes: {
      ...attributes,
      notificationId,
      rollState,
      costRatio: costCash / bonusTotal,
      topUpAdded: bonusTotal - costCash,
      topUpRatio: (bonusTotal - costCash) / bonusTotal,
      pointsToClear: pointsToClear / 1000 / 100,
      maxWinnings: maxWinnings ? maxWinnings : undefined,
      gameMaxBet: gameMaxBet && gameMaxBet < maxIntValue ? gameMaxBet : undefined,
      supportedGamesJson: tryParse(supportedGamesJson)
    }
  };
};
/**
 * Transform input data for this notification type.  Output data is in a consistent format.
 * This allows any type of notification to be used.
 * @param notificationData
 */
const getTransformedInputData = (notificationData: any, locale: string) => {
  const { type: notificationType } = notificationData;
  const typeToTransformMap = {
    [NotificationType.BonusBase]: transformNotificationData,
    [NotificationType.ChestRewardBase]: transformChestReward,
    [NotificationType.FreeSpinsBase]: transformNotificationData,
    [NotificationType.FixedPrizeDrawBase]: transformFixedPrizeDraw,
    [NotificationType.InstantBonusSplitWager]: transformInstantBonus,
    [NotificationType.MiniGameTicket]: (notificationData: BaseMPNotification) => notificationData // No transform needed
  };

  const transformFunction = typeToTransformMap[notificationType as keyof typeof typeToTransformMap];
  if (transformFunction) {
    return transformFunction(notificationData, locale);
  }
  return notificationData;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
type GetAttributes = (
  attributes: MultiPurposeNotification['attributes'],
  displayAttributes: Record<string, any>[]
) => Record<string, any>;
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 *
 * @param attributes - list of server attributes, eg: num of plays, expiration date etc.
 * @param displayAttributes  - list of attributes and config for display on the view
 *
 * @returns List of {key: value} objects where each key is the i18n tag and each value is formatted according to displayAttribute config
 */
const getConfiguredAttributes: GetAttributes = (
  attributes,
  displayAttributes
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any>[] => {
  const displayAttributesList = displayAttributes.map((attrConfig) => {
    const { attrValue = attributes[attrConfig.attrKey as keyof typeof attributes] } = attrConfig;
    const attrFormatValue = attributes[attrConfig.attrFormatKey as keyof typeof attributes]; // e.g. currency code
    return {
      i18nTagCfg: attrConfig.attrLabel,
      value: attrValue,
      formatterValue: attrFormatValue,
      formatterType: attrConfig.attrFormatterType
    };
  });
  return displayAttributesList.filter((attribute) => attribute.value !== undefined);
};

const parseQsNotificationData = (queryString?: string): Nullable<MultiPurposeNotification> => {
  if (!queryString) return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const notificationData: { [key: string]: any } = {};
  const searchParams = new URLSearchParams(queryString);
  for (const [key, value] of searchParams) {
    try {
      notificationData[key] = JSON.parse(value);
    } catch {
      notificationData[key] = value;
    }
  }
  return notificationData as MultiPurposeNotification;
};

/**
 *
 * @param i18nValuesConfig from Notification.type.config, i18nTags.tag.values
 * @param attributes from server response
 * @returns {key: value} map object
 *
 * Usage:
 * <FormattedMessage id={item.id} values={geti18nValueConfig(item.values, attributes)} />
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
const geti18NValueConfig = (
  i18nValuesConfig: Record<string, any>,
  attributes: MultiPurposeNotification['attributes']
) => {
  /* eslint-enable @typescript-eslint/no-explicit-any */
  if (i18nValuesConfig) {
    const valueKeys = Object.keys(i18nValuesConfig);
    const keyValueMap: Values = {};
    valueKeys.forEach((key) => {
      keyValueMap[key] =
        attributes[
          i18nValuesConfig[key as keyof typeof i18nValuesConfig] as keyof typeof attributes
        ];
    });
    return keyValueMap;
  }
  return {};
};

export const formatPercent = (value: number, locale: string, maxFractionDigits = 0) =>
  new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFractionDigits
  }).format(value);

/**
 * Ensure that the url is valid
 * @param url
 * @returns url if the parameter matches the URL regex, else returns null
 */
const parseURL = (url: string) => {
  const testURL = urlParse(url, {});
  if (url && testURL.hostname && testURL.host) {
    return url;
  } else {
    return null;
  }
};

export {
  getChestAssetURL,
  getConfiguredAttributes,
  getTransformedInputData,
  geti18NValueConfig,
  parseURL,
  parseQsNotificationData
};
